import { useState, useMemo } from 'react'
import { Plus, Pencil, Trash2, Search, ChefHat, Package, DollarSign, AlertTriangle, Check, X, Filter } from 'lucide-react'
import {
  getAllPosProducts, savePosProduct, deletePosProduct,
  getRecipes, getRecipe, getRecipeByProductId, saveRecipe, deleteRecipe,
  getInventoryItems, calculateRecipeCost, getProductCostCard
} from '../../lib/posStore'
import { getPosDepartments } from '../../data/posSeed'
import { useDepartment } from '../../context/DepartmentContext'

const EMPTY_PRODUCT = {
  code: '', name: '', department: '', category: '', sellingPrice: '', type: 'manufactured', isActive: true
}

const EMPTY_INGREDIENT = { inventoryItemId: '', qty: '', unit: '' }

function MarginBadge({ margin }) {
  if (margin == null) return null
  const color = margin >= 30 ? 'text-green-400' : margin >= 10 ? 'text-amber-400' : 'text-red-400'
  const bg = margin >= 30 ? 'bg-green-400/10' : margin >= 10 ? 'bg-amber-400/10' : 'bg-red-400/10'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color} ${bg}`}>
      {margin.toFixed(1)}%
    </span>
  )
}

function StatusBadge({ status }) {
  const map = {
    healthy: { label: 'Healthy', color: 'text-green-400 bg-green-400/10' },
    warning: { label: 'Warning', color: 'text-amber-400 bg-amber-400/10' },
    loss: { label: 'Loss-Making', color: 'text-red-400 bg-red-400/10' },
  }
  const s = map[status] || map.warning
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold ${s.color}`}>
      {status === 'healthy' && <Check size={12} />}
      {status === 'warning' && <AlertTriangle size={12} />}
      {status === 'loss' && <X size={12} />}
      {s.label}
    </span>
  )
}

export default function ProductManagement() {
  const { currentDepartmentId } = useDepartment()
  const [activeTab, setActiveTab] = useState('catalog')
  const [searchQuery, setSearchQuery] = useState('')
  const [deptFilter, setDeptFilter] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)

  // Product form state
  const [showProductForm, setShowProductForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [productForm, setProductForm] = useState({ ...EMPTY_PRODUCT })

  // Recipe builder state
  const [selectedProductId, setSelectedProductId] = useState('')
  const [recipeForm, setRecipeForm] = useState({ batchYield: 1, ingredients: [{ ...EMPTY_INGREDIENT }] })
  const [editingRecipe, setEditingRecipe] = useState(null)

  const refresh = () => setRefreshKey(k => k + 1)

  // Data — scoped to the current business department
  const posDepartments = useMemo(() => getPosDepartments(currentDepartmentId), [currentDepartmentId])
  const products = useMemo(() => getAllPosProducts(currentDepartmentId), [refreshKey, currentDepartmentId])
  const inventoryItems = useMemo(() => getInventoryItems(currentDepartmentId), [refreshKey, currentDepartmentId])
  const recipes = useMemo(() => getRecipes(currentDepartmentId), [refreshKey, currentDepartmentId])

  const manufacturedProducts = useMemo(
    () => products.filter(p => p.type === 'manufactured' && p.isActive),
    [products]
  )

  const filteredProducts = useMemo(() => {
    let list = products
    if (deptFilter) list = list.filter(p => p.department === deptFilter)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      )
    }
    return list.sort((a, b) => a.name.localeCompare(b.name))
  }, [products, deptFilter, searchQuery])

  // Cost cards for products that have recipes
  const costCards = useMemo(() => {
    const map = {}
    for (const p of products) {
      const card = getProductCostCard(p.id)
      if (card && card.recipe) map[p.id] = card
    }
    return map
  }, [products, refreshKey])

  // ─── Product CRUD ───

  function openAddProduct() {
    setEditingProduct(null)
    setProductForm({ ...EMPTY_PRODUCT })
    setShowProductForm(true)
  }

  function openEditProduct(product) {
    setEditingProduct(product)
    setProductForm({
      code: product.code || '',
      name: product.name || '',
      department: product.department || '',
      category: product.category || '',
      sellingPrice: product.sellingPrice || '',
      type: product.type || 'manufactured',
      isActive: product.isActive !== false,
    })
    setShowProductForm(true)
  }

  function handleSaveProduct() {
    if (!productForm.code.trim() || !productForm.name.trim() || !productForm.department) return
    const record = {
      ...(editingProduct || {}),
      code: productForm.code.trim(),
      name: productForm.name.trim(),
      department: productForm.department,
      category: productForm.category.trim(),
      sellingPrice: Number(productForm.sellingPrice) || 0,
      type: productForm.type,
      isActive: productForm.isActive,
      departmentId: currentDepartmentId,
    }
    savePosProduct(record)
    setShowProductForm(false)
    setEditingProduct(null)
    setProductForm({ ...EMPTY_PRODUCT })
    refresh()
  }

  function handleDeleteProduct(product) {
    if (!confirm(`Delete "${product.name}"? This cannot be undone.`)) return
    // Also delete associated recipe
    const recipe = getRecipeByProductId(product.id)
    if (recipe) deleteRecipe(recipe.id)
    deletePosProduct(product.id)
    refresh()
  }

  // ─── Recipe CRUD ───

  function loadRecipeForProduct(productId) {
    setSelectedProductId(productId)
    if (!productId) {
      setRecipeForm({ batchYield: 1, ingredients: [{ ...EMPTY_INGREDIENT }] })
      setEditingRecipe(null)
      return
    }
    const existing = getRecipeByProductId(productId)
    if (existing) {
      setEditingRecipe(existing)
      setRecipeForm({
        batchYield: existing.batchYield || 1,
        ingredients: existing.ingredients && existing.ingredients.length > 0
          ? existing.ingredients.map(i => ({ inventoryItemId: i.inventoryItemId || '', qty: i.qty || '', unit: i.unit || '' }))
          : [{ ...EMPTY_INGREDIENT }],
      })
    } else {
      setEditingRecipe(null)
      setRecipeForm({ batchYield: 1, ingredients: [{ ...EMPTY_INGREDIENT }] })
    }
  }

  function updateIngredient(index, field, value) {
    const updated = [...recipeForm.ingredients]
    updated[index] = { ...updated[index], [field]: value }
    // Auto-fill unit from inventory item
    if (field === 'inventoryItemId' && value) {
      const invItem = inventoryItems.find(i => i.id === value)
      if (invItem) updated[index].unit = invItem.unit || ''
    }
    setRecipeForm(prev => ({ ...prev, ingredients: updated }))
  }

  function addIngredientRow() {
    setRecipeForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { ...EMPTY_INGREDIENT }],
    }))
  }

  function removeIngredientRow(index) {
    if (recipeForm.ingredients.length <= 1) return
    setRecipeForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }))
  }

  function handleSaveRecipe() {
    if (!selectedProductId) return
    const product = products.find(p => p.id === selectedProductId)
    if (!product) return

    const validIngredients = recipeForm.ingredients.filter(i => i.inventoryItemId && Number(i.qty) > 0)
    if (validIngredients.length === 0) return

    const record = {
      ...(editingRecipe || {}),
      productId: selectedProductId,
      productCode: product.code,
      name: product.name,
      batchYield: Number(recipeForm.batchYield) || 1,
      departmentId: currentDepartmentId,
      ingredients: validIngredients.map(i => ({
        inventoryItemId: i.inventoryItemId,
        ingredientName: inventoryItems.find(inv => inv.id === i.inventoryItemId)?.name || '',
        qty: Number(i.qty),
        unit: i.unit,
      })),
    }
    saveRecipe(record)
    setEditingRecipe(record)
    refresh()
  }

  function handleDeleteRecipe() {
    if (!editingRecipe) return
    if (!confirm('Delete this recipe?')) return
    deleteRecipe(editingRecipe.id)
    setEditingRecipe(null)
    setRecipeForm({ batchYield: 1, ingredients: [{ ...EMPTY_INGREDIENT }] })
    refresh()
  }

  // ─── Recipe cost calculations ───

  const recipeCostData = useMemo(() => {
    if (!selectedProductId) return null
    const product = products.find(p => p.id === selectedProductId)
    if (!product) return null

    const validIngredients = recipeForm.ingredients.filter(i => i.inventoryItemId && Number(i.qty) > 0)
    if (validIngredients.length === 0) return null

    // Build a temporary recipe object for calculation
    const tempRecipe = {
      batchYield: Number(recipeForm.batchYield) || 1,
      ingredients: validIngredients.map(i => ({
        inventoryItemId: i.inventoryItemId,
        qty: Number(i.qty),
        unit: i.unit,
      })),
    }

    const totalBatchCost = calculateRecipeCost({ ...tempRecipe, batchYield: 1 })
    const batchYield = tempRecipe.batchYield
    const perUnitCost = batchYield > 0 ? totalBatchCost / batchYield : totalBatchCost
    const sellingPrice = product.sellingPrice || 0
    const grossProfit = sellingPrice - perUnitCost
    const grossMargin = sellingPrice > 0 ? (grossProfit / sellingPrice) * 100 : 0
    const status = grossMargin < 0 ? 'loss' : grossMargin < 20 ? 'warning' : 'healthy'

    return { totalBatchCost, batchYield, perUnitCost, sellingPrice, grossProfit, grossMargin, status }
  }, [selectedProductId, recipeForm, products, refreshKey])

  // ─── Render helpers ───

  function getDeptLabel(id) {
    return posDepartments.find(d => d.id === id)?.label || id
  }

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-6 lg:p-8">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package size={24} className="text-brand-500" />
              Product Management
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Manage your product catalog and build recipes for costing
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>{products.length} products</span>
            <span className="text-gray-600">|</span>
            <span>{recipes.length} recipes</span>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 mb-6">
          <button
            onClick={() => setActiveTab('catalog')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'catalog'
                ? 'bg-brand-700 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <Package size={14} />
            Product Catalog
          </button>
          <button
            onClick={() => setActiveTab('recipes')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'recipes'
                ? 'bg-brand-700 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            <ChefHat size={14} />
            Recipe Builder
          </button>
        </div>

        {/* ═══════════════════════════════════════════ */}
        {/* TAB 1: PRODUCT CATALOG                      */}
        {/* ═══════════════════════════════════════════ */}
        {activeTab === 'catalog' && (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by name, code, or category..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
                />
              </div>
              <div className="relative">
                <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <select
                  value={deptFilter}
                  onChange={e => setDeptFilter(e.target.value)}
                  className="pl-8 pr-8 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-white appearance-none focus:outline-none focus:border-brand-600"
                >
                  <option value="">All Departments</option>
                  {posDepartments.map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={openAddProduct}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <Plus size={14} />
                Add Product
              </button>
            </div>

            {/* Inline Product Form */}
            {showProductForm && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Code</label>
                    <input
                      type="text"
                      value={productForm.code}
                      onChange={e => setProductForm(f => ({ ...f, code: e.target.value }))}
                      placeholder="e.g. SB30"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Name</label>
                    <input
                      type="text"
                      value={productForm.name}
                      onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Product name"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Department</label>
                    <select
                      value={productForm.department}
                      onChange={e => setProductForm(f => ({ ...f, department: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white appearance-none focus:outline-none focus:border-brand-600"
                    >
                      <option value="">Select department</option>
                      {posDepartments.map(d => (
                        <option key={d.id} value={d.id}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Category</label>
                    <input
                      type="text"
                      value={productForm.category}
                      onChange={e => setProductForm(f => ({ ...f, category: e.target.value }))}
                      placeholder="e.g. bread, meat"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Selling Price (R)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productForm.sellingPrice}
                      onChange={e => setProductForm(f => ({ ...f, sellingPrice: e.target.value }))}
                      placeholder="0.00"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Type</label>
                    <select
                      value={productForm.type}
                      onChange={e => setProductForm(f => ({ ...f, type: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white appearance-none focus:outline-none focus:border-brand-600"
                    >
                      <option value="manufactured">Manufactured</option>
                      <option value="resale">Resale</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <button
                        type="button"
                        onClick={() => setProductForm(f => ({ ...f, isActive: !f.isActive }))}
                        className={`w-10 h-5 rounded-full relative transition-colors ${
                          productForm.isActive ? 'bg-brand-600' : 'bg-gray-700'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                            productForm.isActive ? 'left-5' : 'left-0.5'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-gray-300">
                        {productForm.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={handleSaveProduct}
                    className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Check size={14} />
                    {editingProduct ? 'Update' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setShowProductForm(false); setEditingProduct(null) }}
                    className="flex items-center gap-1.5 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
                  >
                    <X size={14} />
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Product Table */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Code</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-right">Price</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Margin</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {filteredProducts.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                          No products found.
                        </td>
                      </tr>
                    )}
                    {filteredProducts.map(product => {
                      const costCard = costCards[product.id]
                      return (
                        <tr key={product.id} className="hover:bg-gray-800/30 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-gray-300">{product.code}</td>
                          <td className="px-4 py-3 font-medium text-white">{product.name}</td>
                          <td className="px-4 py-3 text-gray-400">{getDeptLabel(product.department)}</td>
                          <td className="px-4 py-3 text-gray-400 capitalize">{product.category || '—'}</td>
                          <td className="px-4 py-3 text-right font-medium">
                            R{(product.sellingPrice || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs ${
                              product.type === 'manufactured' ? 'text-blue-400' : 'text-gray-400'
                            }`}>
                              {product.type === 'manufactured' ? <ChefHat size={12} /> : <Package size={12} />}
                              {product.type === 'manufactured' ? 'Mfg' : 'Resale'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {costCard ? (
                              <MarginBadge margin={costCard.grossMargin} />
                            ) : (
                              <span className="text-xs text-gray-600">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block w-2 h-2 rounded-full ${
                              product.isActive !== false ? 'bg-green-400' : 'bg-gray-600'
                            }`} title={product.isActive !== false ? 'Active' : 'Inactive'} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => openEditProduct(product)}
                                className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product)}
                                className="p-1.5 rounded hover:bg-red-900/30 text-gray-400 hover:text-red-400 transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════ */}
        {/* TAB 2: RECIPE BUILDER                       */}
        {/* ═══════════════════════════════════════════ */}
        {activeTab === 'recipes' && (
          <div className="space-y-4">
            {/* Product Selector */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider font-semibold">
                Select a manufactured product
              </label>
              <select
                value={selectedProductId}
                onChange={e => loadRecipeForProduct(e.target.value)}
                className="w-full sm:w-96 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white appearance-none focus:outline-none focus:border-brand-600"
              >
                <option value="">Choose product...</option>
                {manufacturedProducts.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.name} (R{(p.sellingPrice || 0).toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            {/* Recipe Form */}
            {selectedProductId && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left: Ingredient Form */}
                <div className="lg:col-span-2 bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <ChefHat size={16} className="text-brand-500" />
                      Recipe Ingredients
                      {editingRecipe && (
                        <span className="text-xs text-gray-500 font-normal">(editing existing)</span>
                      )}
                    </h3>
                    {editingRecipe && (
                      <button
                        onClick={handleDeleteRecipe}
                        className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 size={12} />
                        Delete Recipe
                      </button>
                    )}
                  </div>

                  {/* Batch Yield */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-400 whitespace-nowrap">Batch Yield:</label>
                    <input
                      type="number"
                      min="1"
                      value={recipeForm.batchYield}
                      onChange={e => setRecipeForm(f => ({ ...f, batchYield: e.target.value }))}
                      className="w-24 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-brand-600"
                    />
                    <span className="text-xs text-gray-500">units per batch</span>
                  </div>

                  {/* Ingredient Rows */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 font-semibold uppercase tracking-wider px-1">
                      <div className="col-span-5">Ingredient</div>
                      <div className="col-span-3">Quantity</div>
                      <div className="col-span-3">Unit</div>
                      <div className="col-span-1"></div>
                    </div>
                    {recipeForm.ingredients.map((ing, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">
                          <select
                            value={ing.inventoryItemId}
                            onChange={e => updateIngredient(idx, 'inventoryItemId', e.target.value)}
                            className="w-full px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white appearance-none focus:outline-none focus:border-brand-600"
                          >
                            <option value="">Select ingredient...</option>
                            {inventoryItems.map(item => (
                              <option key={item.id} value={item.id}>
                                {item.name} ({item.unit})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={ing.qty}
                            onChange={e => updateIngredient(idx, 'qty', e.target.value)}
                            placeholder="0"
                            className="w-full px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="text"
                            value={ing.unit}
                            onChange={e => updateIngredient(idx, 'unit', e.target.value)}
                            placeholder="g, kg, ml..."
                            className="w-full px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-brand-600"
                          />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <button
                            onClick={() => removeIngredientRow(idx)}
                            disabled={recipeForm.ingredients.length <= 1}
                            className="p-1 rounded hover:bg-red-900/30 text-gray-500 hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={addIngredientRow}
                    className="flex items-center gap-1.5 text-xs text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    <Plus size={14} />
                    Add Ingredient
                  </button>

                  {/* Save Button */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-800">
                    <button
                      onClick={handleSaveRecipe}
                      className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      <Check size={14} />
                      Save Recipe
                    </button>
                  </div>
                </div>

                {/* Right: Cost Card Summary */}
                <div className="space-y-4">
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-4">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <DollarSign size={16} className="text-brand-500" />
                      Cost Card
                    </h3>

                    {recipeCostData ? (
                      <>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">Total Batch Cost</span>
                            <span className="text-sm font-medium">
                              R{recipeCostData.totalBatchCost.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">Batch Yield</span>
                            <span className="text-sm font-medium">
                              {recipeCostData.batchYield} units
                            </span>
                          </div>
                          <div className="h-px bg-gray-800" />
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">COGS (per unit)</span>
                            <span className="text-sm font-bold text-white">
                              R{recipeCostData.perUnitCost.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">Selling Price</span>
                            <span className="text-sm font-medium">
                              R{recipeCostData.sellingPrice.toFixed(2)}
                            </span>
                          </div>
                          <div className="h-px bg-gray-800" />
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">Gross Profit</span>
                            <span className={`text-sm font-bold ${
                              recipeCostData.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              R{recipeCostData.grossProfit.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">Gross Margin</span>
                            <MarginBadge margin={recipeCostData.grossMargin} />
                          </div>
                        </div>

                        <div className="pt-3 border-t border-gray-800">
                          <StatusBadge status={recipeCostData.status} />
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-6 text-gray-500 text-xs">
                        <AlertTriangle size={20} className="mx-auto mb-2 text-gray-600" />
                        Add ingredients with quantities to see the cost breakdown.
                      </div>
                    )}
                  </div>

                  {/* Ingredient cost breakdown */}
                  {recipeCostData && (
                    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Ingredient Breakdown
                      </h4>
                      <div className="space-y-2">
                        {recipeForm.ingredients
                          .filter(i => i.inventoryItemId && Number(i.qty) > 0)
                          .map((ing, idx) => {
                            const invItem = inventoryItems.find(i => i.id === ing.inventoryItemId)
                            if (!invItem) return null
                            const unitCost = invItem.lastPurchasePrice || invItem.avgCost || 0
                            let costPerUnit = unitCost
                            if (ing.unit !== invItem.unit) {
                              if (invItem.unit === 'kg' && ing.unit === 'g') costPerUnit = unitCost / 1000
                              else if (invItem.unit === 'L' && ing.unit === 'ml') costPerUnit = unitCost / 1000
                            }
                            const lineCost = costPerUnit * Number(ing.qty)
                            return (
                              <div key={idx} className="flex justify-between items-center text-xs">
                                <span className="text-gray-400 truncate mr-2">
                                  {invItem.name}
                                  <span className="text-gray-600 ml-1">
                                    ({ing.qty} {ing.unit})
                                  </span>
                                </span>
                                <span className="text-gray-300 whitespace-nowrap">
                                  R{lineCost.toFixed(2)}
                                </span>
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!selectedProductId && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-12 text-center">
                <ChefHat size={40} className="mx-auto mb-3 text-gray-700" />
                <p className="text-gray-400 text-sm">
                  Select a manufactured product above to build or edit its recipe.
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  Recipes link inventory ingredients to products for automatic costing.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
